import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CompleteStageInputLine,
  CompleteStageOutputLine,
  ProcessStageRun,
  ReadyStageRow,
} from "@/features/production/types";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const EPS = 1e-6;

function money(v: unknown) {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

/** Stages on open plans that still need execution. */
export function useReadyProductionStages() {
  return useQuery({
    queryKey: ["production_ready_stages"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ReadyStageRow[]> => {
      const { data, error } = await supabase
        .from("process_plan_stages")
        .select(
          `id, stage_index, status, input_source, quality_check_required,
          process_types(name),
          machines(code, name),
          process_plans!inner(id, plan_no, status, plan_date)`,
        )
        .eq("status", "planned")
        .order("stage_index", { ascending: true });
      if (error) throw error;

      return (data ?? [])
        .map((row) => {
          const raw = row as Record<string, unknown>;
          const plan = raw.process_plans as {
            id: string;
            plan_no: string;
            status: string;
            plan_date: string;
          };
          if (!["planned", "in_progress"].includes(plan.status)) return null;
          const pt = raw.process_types as { name?: string } | null;
          const m = raw.machines as { code?: string; name?: string } | null;
          return {
            stage_id: String(raw.id),
            stage_index: Number(raw.stage_index),
            stage_status: String(raw.status),
            process_name: pt?.name ?? null,
            machine_code: m?.code ?? null,
            machine_name: m?.name ?? null,
            input_source: String(raw.input_source),
            process_plan_id: plan.id,
            plan_no: plan.plan_no,
            plan_status: plan.status,
            plan_date: plan.plan_date,
            quality_check_required: Boolean(raw.quality_check_required),
          };
        })
        .filter(Boolean) as ReadyStageRow[];
    },
  });
}

export function useStageRuns(planId?: string) {
  return useQuery({
    queryKey: ["process_stage_runs", planId],
    enabled: isSupabaseConfigured && Boolean(planId),
    queryFn: async (): Promise<ProcessStageRun[]> => {
      const { data, error } = await supabase
        .from("process_stage_runs")
        .select("*")
        .eq("process_plan_id", planId!)
        .order("started_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProcessStageRun[];
    },
  });
}

/** Output batches still in stock from a completed previous stage. */
export async function loadPreviousStageOutputBatches(previousStageId: string) {
  const { data: run, error: runErr } = await supabase
    .from("process_stage_runs")
    .select("id")
    .eq("stage_id", previousStageId)
    .eq("status", "completed")
    .maybeSingle();
  if (runErr) throw runErr;
  if (!run) return [];

  const { data, error } = await supabase
    .from("process_stage_run_outputs")
    .select(
      `id, inventory_batch_id, actual_qty, actual_weight,
      inventory_batches(id, batch_no, quantity, weight, batch_status, quality_status, material_id, materials(name))`,
    )
    .eq("run_id", run.id);
  if (error) throw error;

  return (data ?? [])
    .map((row) => {
      const raw = row as Record<string, unknown>;
      const batch = raw.inventory_batches as {
        id: string;
        batch_no: string;
        quantity: number;
        weight: number;
        batch_status: string;
        quality_status: string;
        material_id: string;
        materials?: { name?: string } | null;
      } | null;
      if (!batch || batch.batch_status !== "in_stock") return null;
      if (batch.quality_status !== "passed") return null;
      if (money(batch.weight) <= EPS && money(batch.quantity) <= EPS) return null;
      return {
        inventory_batch_id: batch.id,
        batch_no: batch.batch_no,
        material_name: batch.materials?.name ?? null,
        available_qty: money(batch.quantity),
        available_weight: money(batch.weight),
      };
    })
    .filter(Boolean) as {
    inventory_batch_id: string;
    batch_no: string;
    material_name: string | null;
    available_qty: number;
    available_weight: number;
  }[];
}

export function useCompleteStageRun() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      stageId,
      notes,
      inputs,
      outputs,
    }: {
      stageId: string;
      notes?: string;
      inputs: CompleteStageInputLine[];
      outputs: CompleteStageOutputLine[];
    }) => {
      if (!isSupabaseConfigured) throw new Error("Supabase is not configured");

      const activeInputs = inputs.filter(
        (i) => money(i.actual_weight) > 0 || money(i.actual_qty) > 0,
      );
      const activeOutputs = outputs.filter(
        (o) => money(o.actual_weight) > 0 || money(o.actual_qty) > 0,
      );
      if (!activeOutputs.length) throw new Error("Enter at least one output with qty or weight");

      const { data, error } = await supabase.rpc("complete_stage_run", {
        p_stage_id: stageId,
        p_inputs: activeInputs.map((i) => ({
          inventory_batch_id: i.inventory_batch_id,
          process_plan_input_id: i.process_plan_input_id || null,
          actual_qty: money(i.actual_qty),
          actual_weight: money(i.actual_weight),
        })),
        p_outputs: activeOutputs.map((o) => ({
          material_id: o.material_id,
          material_grade_id: o.material_grade_id || null,
          shape_size_id: o.shape_size_id || null,
          location_id: o.location_id,
          uom_id: o.uom_id || null,
          process_plan_output_id: o.process_plan_output_id || null,
          actual_qty: money(o.actual_qty),
          actual_weight: money(o.actual_weight),
          thickness_mm: o.thickness_mm ?? null,
          width_mm: o.width_mm ?? null,
          length_mm: o.length_mm ?? null,
          wt_per_m: o.wt_per_m ?? null,
        })),
        p_notes: notes?.trim() || null,
        p_user_id: user?.id ?? null,
      });
      if (error) throw error;

      const result = data as {
        run_id: string;
        plan_id: string;
        all_done: boolean;
        batch_nos?: string[];
      };
      return {
        runId: result.run_id,
        planId: result.plan_id,
        allDone: Boolean(result.all_done),
        batchNos: result.batch_nos ?? [],
      };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["process_plans"] });
      await queryClient.invalidateQueries({ queryKey: ["process_plans", result.planId] });
      await queryClient.invalidateQueries({ queryKey: ["process_stage_runs"] });
      await queryClient.invalidateQueries({ queryKey: ["production_ready_stages"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batch_genealogy"] });
      toast.success(
        result.allDone
          ? "Stage complete · plan finished"
          : `Stage complete · ${result.batchNos.length || ""} batch(es)`.trim(),
      );
    },
    onError: (error: Error) => toast.error(error.message || "Execution failed"),
  });
}
